import { useState, useEffect, useRef } from "react";
import { ItemPricing } from "@/hooks/useTrips";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { DollarSign, ChevronDown, ChevronUp } from "lucide-react";
import { SUPPORTED_CURRENCIES, LOYALTY_PROGRAMS, formatCurrency } from "@/lib/currency";
import { safeFormat } from "@/lib/formatDateTime";

type PaymentType = "cash" | "points" | "mix";

interface PriceInputSectionProps {
  pricing: ItemPricing | undefined;
  onChange: (pricing: ItemPricing) => void;
  showRetailPrice?: boolean;
  defaultExpanded?: boolean;
}

export function PriceInputSection({
  pricing,
  onChange,
  showRetailPrice = true,
  defaultExpanded = false,
}: PriceInputSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const isInitialMount = useRef(true);
  const userChangedPaymentType = useRef(false);

  const [paymentType, setPaymentType] = useState<PaymentType>(() => {
    if (!pricing) return "cash";
    if (pricing.is_points_redemption && pricing.price_paid) return "mix";
    if (pricing.is_points_redemption) return "points";
    return "cash";
  });

  // Local state for form fields
  const [pricePaid, setPricePaid] = useState(pricing?.price_paid?.toString() || "");
  const [currency, setCurrency] = useState(pricing?.price_paid_currency || "USD");
  const [taxesFees, setTaxesFees] = useState(pricing?.taxes_fees_paid?.toString() || "");
  const [pointsUsed, setPointsUsed] = useState(pricing?.points_used?.toString() || "");
  const [pointsProgram, setPointsProgram] = useState(pricing?.points_program || "");
  const [retailPrice, setRetailPrice] = useState(pricing?.retail_price?.toString() || "");
  const [retailCurrency, setRetailCurrency] = useState(pricing?.retail_currency || "USD");

  // Update local state when pricing prop changes (only on initial mount or external changes)
  useEffect(() => {
    // Skip if user just changed the payment type (prevents circular update)
    if (userChangedPaymentType.current) {
      userChangedPaymentType.current = false;
      return;
    }

    setPricePaid(pricing?.price_paid?.toString() || "");
    setCurrency(pricing?.price_paid_currency || "USD");
    setTaxesFees(pricing?.taxes_fees_paid?.toString() || "");
    setPointsUsed(pricing?.points_used?.toString() || "");
    setPointsProgram(pricing?.points_program || "");
    setRetailPrice(pricing?.retail_price?.toString() || "");
    setRetailCurrency(pricing?.retail_currency || "USD");

    // Only update payment type on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!pricing) {
        setPaymentType("cash");
      } else if (pricing.is_points_redemption && pricing.price_paid) {
        setPaymentType("mix");
      } else if (pricing.is_points_redemption) {
        setPaymentType("points");
      } else {
        setPaymentType("cash");
      }
    }
  }, [pricing]);

  // Update parent whenever local state changes
  const handleFieldChange = () => {
    const updatedPricing: ItemPricing = {
      ...pricing,
      price_paid: pricePaid ? parseFloat(pricePaid) : undefined,
      price_paid_currency: currency,
      taxes_fees_paid: taxesFees ? parseFloat(taxesFees) : undefined,
      points_used: pointsUsed ? parseInt(pointsUsed, 10) : undefined,
      points_program: pointsProgram || undefined,
      is_points_redemption: paymentType === "points" || paymentType === "mix",
      retail_price: retailPrice ? parseFloat(retailPrice) : undefined,
      retail_currency: retailCurrency || "USD",
    };

    // Clean up based on payment type
    if (paymentType === "cash") {
      delete updatedPricing.points_used;
      delete updatedPricing.points_program;
      delete updatedPricing.is_points_redemption;
    } else if (paymentType === "points") {
      delete updatedPricing.price_paid;
      updatedPricing.taxes_fees_paid = taxesFees ? parseFloat(taxesFees) : undefined;
    }

    onChange(updatedPricing);
  };

  // Call handleFieldChange after state updates
  useEffect(() => {
    handleFieldChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricePaid, currency, taxesFees, pointsUsed, pointsProgram, paymentType, retailPrice, retailCurrency]);

  const handlePaymentTypeChange = (value: PaymentType) => {
    userChangedPaymentType.current = true;
    setPaymentType(value);
    // Reset fields based on new payment type
    if (value === "cash") {
      setPointsUsed("");
      setPointsProgram("");
    } else if (value === "points") {
      setPricePaid("");
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border rounded-lg">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full flex justify-between items-center p-4">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            <span className="font-medium">Pricing</span>
            {pricing?.price_paid && (
              <span className="text-sm text-muted-foreground ml-2">
                ({formatCurrency(pricing.price_paid, pricing.price_paid_currency || "USD")})
              </span>
            )}
          </div>
          {isOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent className="px-4 pb-4 space-y-4">
        {/* Retail Price Input */}
        {showRetailPrice && (
          <div className="space-y-2">
            <Label htmlFor="retail_price">Retail Price (for comparison)</Label>
            <div className="flex gap-2">
              <Input
                id="retail_price"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={retailPrice}
                onChange={(e) => setRetailPrice(e.target.value)}
                className="flex-1"
              />
              <Select value={retailCurrency} onValueChange={setRetailCurrency}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {pricing?.retail_fetched_at && (
              <div className="text-xs text-muted-foreground">
                Last fetched: {safeFormat(pricing.retail_fetched_at, "MMM d, yyyy")}
              </div>
            )}
          </div>
        )}

        {/* Payment Type Selection */}
        <div className="space-y-2">
          <Label>Payment Type</Label>
          <RadioGroup
            value={paymentType}
            onValueChange={(value) => handlePaymentTypeChange(value as PaymentType)}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="cash" id="cash" />
              <Label htmlFor="cash" className="font-normal cursor-pointer">
                Cash
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="points" id="points" />
              <Label htmlFor="points" className="font-normal cursor-pointer">
                Points/Miles
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="mix" id="mix" />
              <Label htmlFor="mix" className="font-normal cursor-pointer">
                Mix
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Cash Fields */}
        {(paymentType === "cash" || paymentType === "mix") && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price_paid">Price Paid</Label>
              <div className="flex gap-2">
                <Input
                  id="price_paid"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={pricePaid}
                  onChange={(e) => setPricePaid(e.target.value)}
                  className="flex-1"
                />
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxes_fees">Taxes & Fees</Label>
              <Input
                id="taxes_fees"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={taxesFees}
                onChange={(e) => setTaxesFees(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Points Fields */}
        {(paymentType === "points" || paymentType === "mix") && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="points_used">Points/Miles Used</Label>
                <Input
                  id="points_used"
                  type="number"
                  min="0"
                  placeholder="50000"
                  value={pointsUsed}
                  onChange={(e) => setPointsUsed(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="points_program">Loyalty Program</Label>
                <Select value={pointsProgram} onValueChange={setPointsProgram}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select program" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOYALTY_PROGRAMS.map((program) => (
                      <SelectItem key={program.name} value={program.name}>
                        {program.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Taxes/fees for points redemptions */}
            {paymentType === "points" && (
              <div className="space-y-2">
                <Label htmlFor="taxes_fees_points">Taxes & Fees Paid</Label>
                <div className="flex gap-2">
                  <Input
                    id="taxes_fees_points"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={taxesFees}
                    onChange={(e) => setTaxesFees(e.target.value)}
                    className="flex-1"
                  />
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPORTED_CURRENCIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
